// --- ENVÍO DEL BLOQUE FORM ---
//
// Un ÚNICO listener delegado de 'submit' atrapa cualquier <form class="ai-form">
// del sitio. Funciona en el sitio PUBLICADO (HTML estático) y en la
// previsualización del editor; queda INERTE en el canvas de edición
// (body.editor-active) para no abrir mailto/WhatsApp mientras se edita.
//
// Camino PRINCIPAL: POST a `/site_lead/` → guarda el Lead en la BBDD del tenant y encola el
// email al dueño (web/views_sites.py::site_lead). Y según `data-recipient-type` del <form>:
//   · whatsapp → SIEMPRE abre https://wa.me/<num>?text=<resumen>, y lo hace ANTES del POST
//     (mientras dura la activación transitoria del gesto, o Safari/iOS lo bloquea como
//     pop-up). No es un fallback: es el canal de aviso elegido, y el backend por WhatsApp no
//     manda nada (solo guarda el Lead).
//   · email    → `mailto:<dest>` SOLO si el backend no aceptó el envío (si lo aceptó, el dueño
//     ya tiene su correo y abrir el cliente de correo sería pedir el mensaje dos veces).
// Si no hay destino configurado, solo muestra el mensaje de éxito. El honeypot _hp descarta
// bots. No importa nada del registry de bloques → sin riesgo de ciclo.
//
// HISTORIA (no volver a poner el gate): el POST al backend estaba condicionado a
// `window.IS_PUBLIC`, y esa bandera la declara SOLO `templates/admin/AIBuilderGenerator/
// ver_sitio.html` (editor y SPA en vivo) — el HTML publicado se sirve con
// `templates/sites/page_detail.html`, que NO la declara. Resultado: en el sitio PUBLICADO,
// que es justo donde escriben los visitantes, la condición era falsa y no se llamaba nunca
// al backend: ni Lead, ni email, ni cola. Solo un `mailto:` que además no se abría cuando el
// bloque no traía destinatario. Ese era el fallo reportado ("relleno el formulario y no me
// llega ningún correo").

// Etiqueta de UI (asunto del mailto) en el idioma de la PÁGINA. i18n.js no importa nada
// (solo lee window.NUBE3I_UI_LABELS) → no reintroduce el ciclo que evita la nota de arriba.
import { t } from '../utils/i18n.js';
// Prefijo de las rutas públicas del tenant (SSOT compartida con core/reserva-live-runtime.js).
import { basePublica } from '../utils/base-publica.js';

// Construye "Etiqueta: valor" por cada control con dato, en orden de aparición.
function construirResumen(form) {
    const lineas = [];
    form.querySelectorAll('[data-field-name]').forEach((ctrl) => {
        const val = (ctrl.value || '').trim();
        if (!val) return;
        const campo = ctrl.closest('.form-field');
        const label = (campo && campo.querySelector('.form-label'))
            ? campo.querySelector('.form-label').textContent.trim()
            : ctrl.getAttribute('data-field-name');
        lineas.push(`${label}: ${val}`);
    });
    return lineas.join('\n');
}

function mostrarExito(form) {
    const ok = form.querySelector('.form-success');
    if (!ok) return;
    ok.classList.add('is-visible');
    setTimeout(() => ok.classList.remove('is-visible'), 6000);
}

// D5: guarda el lead en el backend (BBDD del tenant) y dispara el email al dueño.
// Devuelve true si el backend lo aceptó; false ⇒ el llamante cae al fallback sin servidor.
async function enviarLeadBackend(form, tipo, dest) {
    try {
        const fd = new FormData();
        form.querySelectorAll('[data-field-name]').forEach((ctrl) => {
            const campo = ctrl.closest('.form-field');
            const label = (campo && campo.querySelector('.form-label'))
                ? campo.querySelector('.form-label').textContent.trim()
                : ctrl.getAttribute('data-field-name');
            fd.append(label, ctrl.value || '');
        });
        const hp = form.querySelector('[name="_hp"]');
        fd.append('_hp', hp ? hp.value : '');
        fd.append('_recipient', dest || '');
        fd.append('_recipient_type', tipo);
        fd.append('page_url', location.pathname || '');
        // Prefijo del tenant SEGÚN LA SUPERFICIE (publicado · SPA pública en vivo · editor).
        // La regla completa —y por qué `IS_PUBLIC` manda sobre `BUILDER_BASE`— vive en la
        // cabecera de utils/base-publica.js, que es la SSOT: aquí ya no se decide nada. En el
        // editor el POST da 404 conocido y cae al fallback de abajo (como antes, que ni
        // siquiera llamaba al backend); registrar esa ruta con prefijo sería tocar el routing
        // del ERP y no entra aquí.
        const res = await fetch(basePublica() + '/site_lead/', { method: 'POST', body: fd });
        const data = await res.json().catch(() => ({}));
        return res.ok && data && data.ok === true;
    } catch (_) {
        return false;
    }
}

if (typeof document !== 'undefined' && !window.__aiFormSubmitInstalled) {
    window.__aiFormSubmitInstalled = true;

    document.addEventListener('submit', async (e) => {
        const form = e.target.closest && e.target.closest('.ai-form');
        if (!form) return;

        // Nunca navegamos por el método nativo: lo resolvemos nosotros (backend/mailto/wa).
        e.preventDefault();

        // Inerte mientras se edita el canvas (sí funciona en preview y en publicado).
        if (document.body.classList.contains('editor-active')) return;

        // Honeypot: bot rellenó el campo oculto → fingir éxito y abortar.
        const hp = form.querySelector('[name="_hp"]');
        if (hp && hp.value.trim()) { mostrarExito(form); return; }

        // Validación nativa del navegador (required, type=email, etc.).
        if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

        const tipo = form.getAttribute('data-recipient-type') === 'whatsapp' ? 'whatsapp' : 'email';
        const dest = (form.getAttribute('data-recipient') || '').trim();
        const resumen = construirResumen(form);

        // D5: guardar el lead + notificar al dueño por email (backend). Si el backend lo
        // acepta, NO abrimos mailto/wa (el dueño ya lo recibe); si falla, fallback al
        // comportamiento sin servidor de siempre.
        // Ya NO se gatea a `window.IS_PUBLIC` (ver HISTORIA en la cabecera del módulo): esa
        // bandera no existe en el HTML publicado, así que el gate apagaba el backend justo
        // donde hacía falta. El canvas de edición ya salió por el `return` de arriba.
        // ── WhatsApp: se abre AQUÍ, ANTES de cualquier `await` ────────────────────────────
        // WhatsApp NO es un fallback: es EL CANAL de aviso que eligió el cliente. El backend
        // solo GUARDA el lead (views_sites.site_lead no manda nada por WhatsApp — no puede),
        // así que condicionarlo a `!guardado` dejó de avisar a nadie en cuanto el guardado
        // empezó a funcionar de verdad (al quitar el gate de IS_PUBLIC).
        //
        // Y va antes del `await` POR LA ACTIVACIÓN TRANSITORIA: `window.open` solo lo permite
        // el navegador mientras dura el gesto del usuario que disparó este submit. Después de
        // un `await` ya estamos en OTRA tarea, la activación se ha consumido y Safari/iOS
        // (y Firefox con bloqueo estricto) BLOQUEAN la ventana como pop-up → el visitante veía
        // "gracias por tu mensaje" y el mensaje no salía jamás. Abrirlo primero no cambia nada
        // de lo que recibe el dueño: el Lead se guarda igual dos líneas más abajo.
        if (dest && tipo === 'whatsapp') {
            const num = dest.replace(/[^\d]/g, '');
            if (num) window.open(`https://wa.me/${num}?text=${encodeURIComponent(resumen)}`, '_blank', 'noopener');
        }

        let guardado = await enviarLeadBackend(form, tipo, dest);

        // Con email es al revés que con WhatsApp: si el backend lo aceptó, el dueño ya recibe
        // el correo y abrir un `mailto:` encima sería pedirle al visitante que lo mande otra
        // vez a mano. Solo es fallback (y `location.href` no lo bloquea ningún pop-up blocker).
        if (dest && !guardado && tipo === 'email') {
            const asunto = t('form_mail_subject');
            window.location.href = `mailto:${dest}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(resumen)}`;
        }

        mostrarExito(form);
        try { form.reset(); } catch (_) { /* noop */ }
    });
}
